import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  useToast,
} from "@chakra-ui/react";
import { Facility } from "@metriport/api-sdk/medical/models/facility";
import {
  Actions,
  Features,
  SharedStateProvider,
  SidePanel,
  TcmEncounter,
  TcmEncounterResponse,
  tcmEncounterResponseSchema,
  UpdateTcmEncounterResponse,
  updateTcmEncounterResponseSchema,
  useAnalyticsContext,
} from "@metriport/shared-internal";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiOSS, getOSSBaseURL } from "../../../api/apiOSS";
import { isApiKeyGenerated } from "../../../domain/api-key";
import { isMapiAccessActive, isMedicalSubscriptionActive } from "../../../domain/customer";
import { useAppContext } from "../../contexts/app";
import { useIsUserInSandbox } from "../../shared/useIsUserInSandbox";
import { EncounterDetails } from "../encounter/encounter-details";
import NoApiKey from "../no-api-key";
import NoMapiAccess from "../no-mapi-access";
import { METRIPORT_PRIMARY } from "../shared-logic/style";
import { useMetriportApi } from "../shared-logic/useMetriportApi";
import TransitionsOfCareGrid from "./grid";
import "./grid.css";

const TCM_ENCOUNTER_URL = `/medical/v1/tcm/encounter`;

export const getTcmEncounters = async (pageUrl?: string): Promise<TcmEncounterResponse> => {
  const response = await apiOSS.get(pageUrl ?? TCM_ENCOUNTER_URL);
  const parsedResponse = tcmEncounterResponseSchema.parse(response.data);
  return parsedResponse;
};

export const putTcmEncounterNote = async (
  encounterId: string,
  note: string
): Promise<UpdateTcmEncounterResponse> => {
  const response = await apiOSS.put(`${TCM_ENCOUNTER_URL}/${encounterId}`, {
    freetextNote: note,
  });
  const parsedResponse = updateTcmEncounterResponseSchema.parse(response.data);
  return parsedResponse;
};

export const putTcmDischargeSummaryPath = async (
  encounterId: string,
  filePath: string
): Promise<UpdateTcmEncounterResponse> => {
  const response = await apiOSS.put(`${TCM_ENCOUNTER_URL}/${encounterId}`, {
    dischargeSummaryPath: filePath,
  });
  const parsedResponse = updateTcmEncounterResponseSchema.parse(response.data);
  return parsedResponse;
};

export const getAllTcmEncounters = async (): Promise<TcmEncounter[]> => {
  const allEncounters: TcmEncounter[] = [];
  let currentPageUrl: string | undefined = `${TCM_ENCOUNTER_URL}`;

  do {
    try {
      const response: TcmEncounterResponse = await getTcmEncounters(currentPageUrl);
      allEncounters.push(...response.items);
      currentPageUrl = response.meta.nextPage ?? undefined;
    } catch (error) {
      console.error("Error fetching TCM encounters:", error);
      break;
    }
  } while (currentPageUrl);

  return allEncounters;
};

const updateTcmEncounterNote = async (
  encounterId: string,
  note: string
): Promise<UpdateTcmEncounterResponse> => {
  const response = await putTcmEncounterNote(encounterId, note);
  return response;
};

function TransitionsOfCareContent() {
  const { state } = useAppContext();
  const { isUserInSandbox } = useIsUserInSandbox(state);
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState("");
  const [tableFromTop, setTableFromTopHeight] = useState(0);
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedDays, setSelectedDays] = useState<string>("14");
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TcmEncounter | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<string | undefined>(undefined);
  const Analytics = useAnalyticsContext();
  const metriportApi = useMetriportApi();

  // Debounce ref for note updates
  const debounceRef = useRef<NodeJS.Timeout>();

  const facilitiesQuery = useQuery<Facility[], Error>({
    queryKey: ["facilities"],
    queryFn: () => metriportApi.listFacilities(),
  });

  const tcmEncounterQuery = useQuery<TcmEncounter[], Error>({
    queryKey: ["tcm", "encounter"],
    queryFn: () => getAllTcmEncounters(),
  });

  // Mutation for updating TCM encounter notes
  const updateNoteMutation = useMutation({
    mutationFn: ({ encounterId, note }: { encounterId: string; note: string }) =>
      updateTcmEncounterNote(encounterId, note),
    onSuccess: (_, variables) => {
      const queryKey = ["tcm", "encounter"];

      // Update the cache with the new note
      queryClient.setQueryData(queryKey, (oldData: TcmEncounter[] | undefined) => {
        console.log("oldData", oldData);
        if (!oldData) return oldData;

        return oldData.map(encounter =>
          encounter.id === variables.encounterId
            ? { ...encounter, freetextNote: variables.note }
            : encounter
        );
      });

      // Update selectedRow state if it matches the updated encounter
      setSelectedRow(prevSelectedRow => {
        if (prevSelectedRow && prevSelectedRow.id === variables.encounterId) {
          return { ...prevSelectedRow, freetextNote: variables.note };
        }
        return prevSelectedRow;
      });
    },
    onError: error => {
      console.error("Failed to update note:", error);
      toast({
        title: "Error updating note",
        description: "Failed to save the note. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Mutation for updating TCM encounter discharge summary path
  const updateSummaryPathMutation = useMutation({
    mutationFn: ({ encounterId, title }: { encounterId: string; title: string }) =>
      putTcmDischargeSummaryPath(encounterId, title),
    onSuccess: (_, variables) => {
      const queryKey = ["tcm", "encounter"];

      // Update the cache with the new discharge summary path
      queryClient.setQueryData(queryKey, (oldData: TcmEncounter[] | undefined) => {
        if (!oldData) return oldData;

        return oldData.map(encounter =>
          encounter.id === variables.encounterId
            ? { ...encounter, dischargeSummaryPath: variables.title }
            : encounter
        );
      });

      toast({
        title: "Summary path updated",
        description: "The discharge summary path has been successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: error => {
      console.error("Failed to update summary path:", error);
      toast({
        title: "Error updating summary path",
        description: "Failed to save the summary path. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Debounced note update handler
  const handleNoteUpdate = useCallback(
    (encounterId: string, note: string, immediate = false) => {
      // Clear existing timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (immediate) {
        updateNoteMutation.mutate({ encounterId, note });
        return;
      }

      debounceRef.current = setTimeout(() => {
        updateNoteMutation.mutate({ encounterId, note });
      }, 1000);
    },
    [updateNoteMutation]
  );

  function onSummaryFilePathChange(encounterId: string, title: string) {
    updateSummaryPathMutation.mutate({ encounterId, title });
    return;
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const typeOptions = [
    { label: "Admitted", value: "admitted" },
    { label: "Discharged", value: "discharged" },
  ];

  const filterOptions = [
    { label: "2 Days", value: "2" },
    { label: "1 Week", value: "7" },
    { label: "2 Weeks", value: "14" },
    { label: "4 Weeks", value: "28" },
  ];

  const handleTypeSelect = (value: string) => {
    setSelectedType(value);
  };

  const handleDaySelect = (value: string) => {
    setSelectedDays(value);
  };

  const filteredRows = useMemo(() => {
    if (!tcmEncounterQuery.data) return null;

    let filtered = tcmEncounterQuery.data;

    if (selectedType) {
      filtered = filtered.filter(row => {
        const event = (row.latestEvent ?? "").toLowerCase().trim();
        return event === selectedType.toLowerCase().trim();
      });
    }

    const daysToFilter = parseInt(selectedDays);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToFilter);
    filtered = filtered.filter(row => {
      if (!row.dischargeTime) return true;
      const eventDate = new Date(row.dischargeTime);
      if (isNaN(eventDate.getTime())) return false;

      return eventDate >= cutoffDate;
    });

    return filtered;
  }, [selectedType, selectedDays, tcmEncounterQuery.data]);

  useEffect(() => {
    const element = document.getElementById("table-container");
    if (element) {
      setTableFromTopHeight(element.getBoundingClientRect().top);
    }
  }, [state]);

  const onRowClick = useCallback((row: TcmEncounter) => {
    setSelectedRow(row);
    setShowSidePanel(true);
  }, []);

  if (tcmEncounterQuery.isLoading) {
    return <div>Loading...</div>;
  }

  const apiKeyGenerated = isApiKeyGenerated(state.keyIds);

  if (state.isLoaded && !apiKeyGenerated) {
    return <NoApiKey />;
  }

  if (
    state.isLoaded &&
    apiKeyGenerated &&
    (!isMedicalSubscriptionActive(state, isUserInSandbox) ||
      !isMapiAccessActive(state, isUserInSandbox))
  ) {
    return <NoMapiAccess />;
  }

  if (state.isLoaded) {
    return (
      <Flex p={10}>
        <Box px={5} flexGrow={1}>
          <Flex alignItems={"center"} justifyContent={"space-between"}>
            <Heading size="xl">Patient Transitions of Care</Heading>
          </Flex>
          <Divider my={6} />
          <Flex justifyContent={"space-between"} alignItems={"center"} mb={5}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" color="gray.300" fontSize="1.2em">
                <SearchIcon />
              </InputLeftElement>
              <Input
                minW={"300px"}
                w={"25%"}
                value={search}
                placeholder="Search encounters"
                onChange={e => setSearch(e.target.value)}
                onClick={() => Analytics.emit(Actions.search, Features.facility)}
                _hover={{ borderColor: METRIPORT_PRIMARY }}
                _active={{ borderColor: METRIPORT_PRIMARY }}
                _focus={{ borderColor: METRIPORT_PRIMARY }}
              />
            </InputGroup>
            <Flex gap={4}>
              <Select
                placeholder="Select facility"
                w={"180px"}
                bg={"white"}
                css={{ textOverflow: "ellipsis" }}
                value={facilitiesQuery.isLoading ? "Loading..." : selectedFacility}
                onChange={e => setSelectedFacility(e.target.value)}
              >
                {facilitiesQuery.data?.map(facility => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </Select>
              <Select
                placeholder="Pick type"
                w={"120px"}
                bg={"white"}
                css={{ textOverflow: "ellipsis" }}
                value={selectedType}
                onChange={e => handleTypeSelect(e.target.value)}
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Select
                placeholder="Pick days"
                w={"120px"}
                bg={"white"}
                css={{ textOverflow: "ellipsis" }}
                value={selectedDays}
                onChange={e => handleDaySelect(e.target.value)}
              >
                {filterOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Flex>
          </Flex>
          <Box
            id="table-container"
            style={{ width: "100%", height: `calc(95vh - ${tableFromTop}px)` }}
          >
            <TransitionsOfCareGrid
              rows={filteredRows}
              search={search}
              isLoading={tcmEncounterQuery.isLoading}
              onRowClick={onRowClick}
              onNoteUpdate={handleNoteUpdate}
            />
          </Box>
        </Box>
        <SidePanel showPanel={showSidePanel} setSidePanelContent={() => setShowSidePanel(false)}>
          {selectedRow && (
            <EncounterDetails
              encounter={selectedRow}
              onNoteChange={(note, immediate) => handleNoteUpdate(selectedRow.id, note, immediate)}
              onSummaryFilePathChange={summaryFilePath =>
                onSummaryFilePathChange(selectedRow.id, summaryFilePath)
              }
            />
          )}
        </SidePanel>
      </Flex>
    );
  }

  return null;
}

export default function TransitionsOfCare() {
  const { state } = useAppContext();
  const { isUserInSandbox } = useIsUserInSandbox(state);

  if (!state.isLoaded) return null;

  const baseUrl = useMemo(() => {
    return getOSSBaseURL(isUserInSandbox);
  }, [isUserInSandbox]);

  return (
    <SharedStateProvider authToken={state.authToken} baseUrl={baseUrl} ehrDetails={undefined}>
      <TransitionsOfCareContent />
    </SharedStateProvider>
  );
}
